import { ReactNode, useCallback, useMemo, useState } from "react";
import { useApp } from "../../../context/AppContextProvider";
import { TourContext, TourContextValue } from "./tourContext";
import { getTour } from "./tourRegistry";
import TourOverlay from "./TourOverlay";

// localStorage flag for the first-run intro tour. Lives in the device-pref family
// (theme / language) so it survives logout — handleStateLogout only clears tokens.
export const INTRO_TOUR_SEEN_KEY = "learnerIntroTourSeen";

const TourProvider = ({ children }: { children?: ReactNode }) => {
  const { setTourForceNavExpanded } = useApp();
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const activeTour = activeTourId ? getTour(activeTourId) : undefined;
  const isActive = !!activeTour;

  const startTour = useCallback(
    (tourId: string) => {
      const tour = getTour(tourId);
      if (!tour) return;
      setStepIndex(0);
      setActiveTourId(tourId);
      // Only pin the nav open when the tour actually spotlights nav items (the
      // intro). Per-screen tours anchor to the page, so leave the nav alone.
      const needsNav = tour.steps.some((s) => s.anchorId.startsWith("nav-"));
      setTourForceNavExpanded(needsNav);
    },
    [setTourForceNavExpanded],
  );

  const stop = useCallback(() => {
    // Mark the intro tour seen whether finished or skipped — never auto-run twice.
    setActiveTourId((current) => {
      if (current === "intro") {
        try {
          localStorage.setItem(INTRO_TOUR_SEEN_KEY, "true");
        } catch {
          /* ignore storage failures */
        }
      }
      return null;
    });
    setStepIndex(0);
    setTourForceNavExpanded(false);
  }, [setTourForceNavExpanded]);

  const next = useCallback(() => {
    if (!activeTour) return;
    if (stepIndex < activeTour.steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      stop();
    }
  }, [activeTour, stepIndex, stop]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value: TourContextValue = useMemo(
    () => ({
      isActive,
      activeTour,
      stepIndex,
      startTour,
      next,
      prev,
      stop,
    }),
    [isActive, activeTour, stepIndex, startTour, next, prev, stop],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourOverlay />
    </TourContext.Provider>
  );
};

export default TourProvider;
